import { Flex, Grid, Pagination, Table, Tag, Typography, theme } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue } from '@/shared/lib/money'
import { SubscriberStateTag } from '@/shared/ui/StatusTag'
import { RowActions, type RowAction } from '@/shared/ui/RowActions'
import type { SubscriberRow } from './subscriberApi'

interface SubscriberListProps {
  rows: readonly SubscriberRow[]
  total: number
  /** Nolinchidan boshlanadi — backend `page` parametri bilan bir xil */
  page: number
  size: number
  loading: boolean
  onPageChange: (page: number, size: number) => void
  onOpen: (row: SubscriberRow) => void
  /** Qator menyusining tarkibi — chaqiruvchi yasaydi */
  actionsFor: (row: SubscriberRow) => readonly RowAction[]
}

/**
 * Qatorning SAHIFAGA MOS tartib raqami (egasi talabi, 2026-08-17).
 *
 * ⚠️ Indeksning o'zi yetarli emas: uchinchi sahifada u yana 1 dan
 * boshlanardi va «21-qator» degan gap ikki odam uchun ikki xil qator
 * bo'lib qolardi.
 */
function ordinal(page: number, size: number, index: number): number {
  return page * size + index + 1
}

function BalanceText({ row }: { row: SubscriberRow }) {
  const { token } = theme.useToken()
  const negative = row.balance !== null && row.balance.amount < 0
  return (
    <Typography.Text
      strong
      style={{ whiteSpace: 'nowrap', color: negative ? token.colorError : undefined }}
    >
      {formatMoneyValue(row.balance)}
    </Typography.Text>
  )
}

/**
 * Telefon ko'rinishi — jadval EMAS, bosiladigan karta.
 *
 * ⚠️ Reseller asosan kompyuterda ishlaydi, lekin ilova telefonda ham
 * ochiladi. Yetti ustunli jadval 390px kenglikda gorizontal scroll'ga
 * tushadi va eng kerakli ikki qiymat (login va balans) bir vaqtda
 * ko'rinmay qoladi — ya'ni har qatorni ikki marta surib o'qish kerak
 * bo'lardi. Kartada ular bitta qatorda, ikki chekkada.
 */
function MobileRow({
  row,
  index,
  page,
  size,
  onOpen,
  actions,
}: {
  row: SubscriberRow
  index: number
  page: number
  size: number
  onOpen: (row: SubscriberRow) => void
  actions: readonly RowAction[]
}) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const s = row.subscriber

  return (
    <div
      style={{
        padding: '10px 12px',
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
      }}
    >
      <Flex align="center" justify="space-between" gap={8}>
        <Flex
          align="center"
          gap={8}
          style={{ minWidth: 0, cursor: 'pointer', flex: 1 }}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onOpen(row)
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {ordinal(page, size, index)}
          </Typography.Text>
          <Typography.Text strong ellipsis style={{ fontSize: 16 }}>
            {s.login}
          </Typography.Text>
        </Flex>
        <BalanceText row={row} />
        <RowActions actions={actions} ariaLabel={t('common.actions')} />
      </Flex>

      <Typography.Paragraph
        type="secondary"
        ellipsis={{ rows: 1 }}
        style={{ margin: '2px 0 6px', fontSize: 13 }}
      >
        {[s.displayName, s.phone].filter(Boolean).join(' · ') || t('common.dash')}
      </Typography.Paragraph>

      <Flex align="center" gap={6} wrap>
        <SubscriberStateTag state={s.state} />
        <Tag>{row.planName ?? t('subscribers.noPlan')}</Tag>
      </Flex>
    </div>
  )
}

/**
 * Abonentlar ro'yxati.
 *
 * ⚠️ Ustun sozlash, eksport va ommaviy amal ATAYLAB yo'q
 * (11-SHERIK-KABINETI-UI.md §9.7): ular admin `DataTable` ining
 * qismlari va u bu ilovaga olib kelinmagan. Kerak bo'lgani —
 * tartib raqami, ma'lumot va bitta amal menyusi.
 */
export function SubscriberList({
  rows,
  total,
  page,
  size,
  loading,
  onPageChange,
  onOpen,
  actionsFor,
}: SubscriberListProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  if (isMobile) {
    return (
      <Flex vertical gap={10}>
        {rows.map((row, index) => (
          <MobileRow
            key={row.subscriber.id}
            row={row}
            index={index}
            page={page}
            size={size}
            onOpen={onOpen}
            actions={actionsFor(row)}
          />
        ))}
        <Pagination
          align="center"
          current={page + 1}
          pageSize={size}
          total={total}
          showSizeChanger={false}
          onChange={(next, nextSize) => onPageChange(next - 1, nextSize)}
        />
      </Flex>
    )
  }

  const columns: ColumnsType<SubscriberRow> = [
    {
      title: '#',
      key: 'ordinal',
      width: 56,
      render: (_value, _row, index) => (
        <Typography.Text type="secondary">{ordinal(page, size, index)}</Typography.Text>
      ),
    },
    {
      title: t('subscribers.columns.login'),
      key: 'login',
      render: (_value, row) => (
        <Typography.Text strong copyable={{ text: row.subscriber.login }}>
          {row.subscriber.login}
        </Typography.Text>
      ),
    },
    {
      title: t('subscribers.columns.person'),
      key: 'person',
      render: (_value, row) => (
        <Flex vertical>
          <Typography.Text>{row.subscriber.displayName ?? t('common.dash')}</Typography.Text>
          {row.subscriber.profile?.customerId ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.subscriber.profile.customerId}
            </Typography.Text>
          ) : null}
          {row.subscriber.phone ? (
            // Bosiladigan raqam: kompyuterda ham `tel:` havolasi raqamni
            // nusxalashdan tez, telefonda esa to'g'ridan-to'g'ri qo'ng'iroq.
            <Typography.Link href={`tel:${row.subscriber.phone}`}>
              {row.subscriber.phone}
            </Typography.Link>
          ) : null}
        </Flex>
      ),
    },
    {
      title: t('subscribers.columns.plan'),
      key: 'plan',
      render: (_value, row) => <Tag>{row.planName ?? t('subscribers.noPlan')}</Tag>,
    },
    {
      title: t('common.balance'),
      key: 'balance',
      align: 'right',
      render: (_value, row) => <BalanceText row={row} />,
    },
    {
      title: t('subscribers.columns.state'),
      key: 'state',
      render: (_value, row) => <SubscriberStateTag state={row.subscriber.state} />,
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      render: (_value, row) => (
        <RowActions actions={actionsFor(row)} ariaLabel={t('common.actions')} />
      ),
    },
  ]

  return (
    <Table<SubscriberRow>
      rowKey={(row) => row.subscriber.id}
      dataSource={rows}
      columns={columns}
      loading={loading}
      size="middle"
      // ⚠️ Butun qator bosiladi, alohida «Ochish» tugmasi yo'q: u yettinchi
      // ustunni qo'shardi va uch nuqta menyusi bilan yonma-yon turib,
      // «qaysi biri kartochkani ochadi» degan savol tug'dirardi.
      onRow={(row) => ({ onClick: () => onOpen(row), style: { cursor: 'pointer' } })}
      pagination={{
        current: page + 1,
        pageSize: size,
        total,
        showSizeChanger: true,
        pageSizeOptions: [20, 50, 100],
        onChange: (next, nextSize) => onPageChange(next - 1, nextSize),
      }}
    />
  )
}
